import { Button, Text } from '@/ui-kit'
import { AxiosError } from 'axios'
import { FileText, Trash2 } from 'lucide-react'
import { ChangeEvent, useRef, useState } from 'react'
import { useUploadSubmission } from '../api'

const FileUploader = ({
  missionId,
  buttonText,
  onUploadSuccess
}: {
  missionId: string
  buttonText?: string
  onUploadSuccess: () => void
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { mutateAsync: uploadSubmission } = useUploadSubmission()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      setSelectedFile(event.target.files[0])
      setErrorMessage('')
    }
  }

  const handleChooseFile = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setErrorMessage('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUpload = async (event: any) => {
    event.preventDefault()
    if (selectedFile && event.target) {
      setErrorMessage('')
      try {
        const formData = new FormData(event.target)
        formData.append('file', selectedFile)
        await uploadSubmission({
          missionId,
          data: formData
        })
        setSelectedFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        onUploadSuccess()
      } catch (err) {
        const error = err as AxiosError
        const message = (error.response?.data as { error?: string })?.error || error.message || 'Failed to upload file'
        setErrorMessage(message)
      }
    }
  }

  return (
    <form onSubmit={handleUpload} className="flex flex-col gap-8">
      <input ref={fileInputRef} type="file" onChange={handleFileChange} className="hidden" accept="*/*" />
      <div className="flex flex-col gap-2">
        {selectedFile && (
          <div className="flex items-center gap-2 rounded-lg p-1">
            <Text type="body1-regular" className="break-all">
              {selectedFile.name}
            </Text>
            <button type="button" onClick={handleRemoveFile}>
              <Trash2 size={18} />
            </button>
          </div>
        )}
        <Button
          type="button"
          variant="bordered"
          size="small"
          onClick={handleChooseFile}
          className="flex max-w-min items-center gap-2"
        >
          <FileText size={18} /> {buttonText || 'Choose file'}
        </Button>
      </div>
      {errorMessage && (
        <Text type="body3-regular" className="colorError">
          {errorMessage}
        </Text>
      )}
      <Button type="submit" variant="default" className="w-[300px] max-mobile:w-full max-mobile:max-w-full" isDisabled={!selectedFile}>
        Verify
      </Button>
    </form>
  )
}

export default FileUploader
